<script setup lang="ts">
const { $notification, $cookies } = useNuxtApp();
const notification = $notification;
const cookies = $cookies;

const notificationsModal = ref<boolean>(false);

const notificationAsk = async (e: Event): Promise<void> => {
  await notification?.value?.ask(e);
  if (Notification.permission !== 'default')
    notificationsModal.value = true;
};

const facebookModal = ref<boolean>(false);
</script>

<template>
  <div>
    <div class="fixed bottom-2 right-2 xl:bottom-4 xl:right-4 flex flex-col justify-center items-end gap-2 xl:gap-3 2xl:gap-4">
      <BaseText
        v-if="cookies?.consent?.value"
        @click="(e: Event) => (cookies.consent.value.consent = null)"
        size="xl"
        class="!text-xl drop-shadow-lg hover:drop-shadow-2xl scale-[1.0] hover:scale-[1.5] [&&]:duration-200 cursor-pointer hover:mb-1 hover:mr-1"
        value="🍪" />
      <BaseButton
        class="drop-shadow-lg hover:drop-shadow-2xl scale-[1.0] hover:scale-[1.1] [&&]:duration-200"
        style-name="primary"
        @click="notificationAsk">
        <Icon v-if="notification?.status" name="bx:notification-off" />
        <Icon v-else name="bx:notification" />
      </BaseButton>
      <BaseButton
        v-if="useRuntimeConfig()?.public?.info?.fbPageUrl"
        class="relative flex drop-shadow-lg hover:drop-shadow-2xl scale-[1.0] hover:scale-[1.1] [&&]:duration-200"
        style-name="primary"
        @click="(e: Event) => (facebookModal = true)">
        <Icon name="uil:facebook-f" class="absolute inline-flex h-full w-full animate-ping animate-pause-[10000ms] opacity-75" />
        <Icon name="uil:facebook-f" class="relative inline-flex" />
      </BaseButton>
      <BaseButton
        v-if="useRuntimeConfig()?.public?.contact?.mail"
        :href="`mailto:${useRuntimeConfig()?.public?.contact?.mail}`"
        class="drop-shadow-lg hover:drop-shadow-2xl scale-[1.0] hover:scale-[1.1] [&&]:duration-200"
        style-name="primary">
        <Icon name="uil:envelope" />
      </BaseButton>
      <BaseButton
        v-if="useRuntimeConfig()?.public?.contact?.phone"
        :href="`tel:${useRuntimeConfig()?.public?.contact?.phone}`"
        class="drop-shadow-lg hover:drop-shadow-2xl scale-[1.0] hover:scale-[1.1] [&&]:duration-200"
        style-name="primary">
        <Icon name="uil:phone" />
      </BaseButton>
    </div>
    <BaseModal
      v-if="useRuntimeConfig()?.public?.info?.fbPageUrl"
      v-model="facebookModal"
      bg-click-close show-default-close
      :class-name="[
        ('max-w-' + (['2xl'].includes(useNuxtApp()?.$styleSize?.value) ? 'xl' : useNuxtApp()?.$styleSize?.value)),
        ('shadow-' + (['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')),
        'bg-white text-center flex flex-col justify-center items-center gap-2 xl:gap-4 overflow-auto'
      ]">
      <BaseFBPageFull :page-url="useRuntimeConfig()?.public?.info?.fbPageUrl" />
    </BaseModal>
    <BaseModal
      v-model="notificationsModal"
      bg-click-close show-default-close
      :size="(['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')"
      :class-name="[
        ('max-w-' + (['2xl'].includes(useNuxtApp()?.$styleSize?.value) ? 'xl' : useNuxtApp()?.$styleSize?.value)),
        ('shadow-' + (['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')),
        'bg-white text-center flex flex-col justify-center items-center gap-2 xl:gap-4 overflow-auto'
      ]">
      <div class="hidden lg:block text-nowrap">
        <span v-if="notification?.status" v-text="'🔕'" />
        <span v-else v-text="'🔔'" />
      </div>
      <div class="flex flex-col justify-center items-center gap-2 xl:gap-4 w-full h-full">
        <BaseText
          class="font-semibold"
          :size="(['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')">
          <h1>
            Ako {{ notification?.status ? 'vypnúť' : 'zapnúť' }} notifikácie
          </h1>
        </BaseText>
        <BaseText>
          Povolenie na zasielanie notifikácií nám bolo udelené vo Vašom prehliadači (webové stránky ho nemôžu zrušiť samy).<br/>
          Ak chcete notifikácie vypnúť, musíte to urobiť <span class="font-bold">manuálne</span> v nastaveniach prehliadača.
        </BaseText>
        <BaseText>
          <ol class="pl-6 font-normal text-left list-decimal list-outside space-y-3 text-sm md:text-base ">
            <li>
              Kliknite na ikonu zámku (<span class="font-semibold">🔒</span>) alebo ikonu informácií (<span class="font-semibold">ⓘ</span>).<br/>
              <i>
                Ikona sa nachádza hneď vedľa adresy našej stránky v adresnom riadku prehliadača.
              </i>
            </li>
            <li>
              Zobrazí sa menu s nastaveniami pre túto stránku.<br/>
              Nájdite položku <span class="font-semibold">„Upozornenia“</span> alebo <span class="font-semibold">„Notifikácie“</span>.
            </li>
            <li>
              Zmeňte na <span class="font-semibold">„Povoliť“</span>, <span class="font-semibold">„Blokovať“</span> alebo <span class="font-semibold">„Pýtať sa“</span>.
            </li>
            <li>
              Obnovte <span class="font-semibold">🔄</span> (refreshnite) túto stránku, aby sa zmena prejavila.
            </li>
            <li>
              <span class="font-semibold text-primary">Nainštalujte si aplikáciu (PWA):</span>
              <ul class="pl-5 mt-2 xl:mt-4 list-disc space-y-1 xl:space-y-2 opacity-90 text-sm">
                <li>
                  <span class="font-semibold">Google Chrome / Edge</span><br/> V adresnom riadku vpravo kliknite na ikonu <span class="font-semibold">Inštalovať (⊕)</span> alebo v menu <span class="font-semibold">(⋮)</span> zvoľte <span class="font-semibold">„Inštalovať aplikáciu“</span>.
                </li>
                <li>
                  <span class="font-semibold">Firefox (Android)</span><br/> Kliknite na <span class="font-semibold">tri bodky (⋮)</span> a zvoľte <span class="font-semibold">„Inštalovať“</span> alebo kliknite na ikonu <span class="font-semibold">domčeka s pluskom (+)</span> priamo v adresnom riadku.
                </li>
                <li>
                  <span class="font-semibold">Firefox (Desktop)</span><br/> Keďže Firefox nepodporuje priamu inštaláciu PWA, odporúčame si stránku <span class="font-semibold">pripnúť (Pin Tab)</span> alebo vytvoriť odkaz na plochu potiahnutím ikony zámku na vašu plochu.
                </li>
              </ul>
            </li>
            <li>
              Po inštalácii otvorte aplikáciu priamo z vašej plochy.
            </li>
          </ol>
        </BaseText>
      </div>
      <div class="hidden lg:block text-nowrap">
        <span v-if="notification?.status" v-text="'🔕'" />
        <span v-else v-text="'🔔'" />
      </div>
    </BaseModal>
  </div>
</template>
